import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { showToast } from '@/utils/toast';

const DEFAULT_MAX_PAGES = 10;
const ABS_MAX_PAGES = 100;

/**
 * 同步更新页数上限弹窗（议价单等：无时间窗、仅 pageNum 翻页的接口）。
 */
export default function SheinSyncMaxPagesModal({
  open,
  confirming = false,
  title = fixedUi("同步更新"),
  hint = '',
  pageSizeHint = 200,
  defaultMaxPages = DEFAULT_MAX_PAGES,
  absMaxPages = ABS_MAX_PAGES,
  onConfirm,
  onClose,
}) {
  const [maxPages, setMaxPages] = useState(String(defaultMaxPages));
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setMaxPages(String(defaultMaxPages || DEFAULT_MAX_PAGES));
  }, [open, defaultMaxPages]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !confirming && !submitting) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, confirming, onClose]);

  if (!open) return null;

  const cap = Math.max(1, Number(absMaxPages) || ABS_MAX_PAGES);
  const busy = confirming || submitting;

  const handleConfirm = async () => {
    if (confirming || submittingRef.current) return;
    const pages = Number(maxPages);
    if (!Number.isFinite(pages) || pages < 1) {
      showToast(fixedUi("请填写有效的同步分页数（≥1）"), 'error');
      return;
    }
    if (pages > cap) {
      showToast(`同步分页数最多 ${cap}`, 'error');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onConfirm?.({
        maxPages: Math.min(cap, Math.max(1, Math.floor(pages))),
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="spo-batch-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={fixedUi("关闭")}
        disabled={busy}
        onClick={() => !busy && onClose?.()}
      />
      <div
        className="spo-batch-modal__panel spo-sync-range-modal__panel spo-product-sync-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-sync-max-pages-modal-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-sync-max-pages-modal-title">{title}</h2>
          <button
            type="button"
            className="spo-btn spo-btn--ghost spo-btn--sm"
            disabled={busy}
            onClick={onClose}
          >
            {fixedUi("关闭")}
          </button>
        </div>
        <div className="spo-batch-modal__body">
          {hint ? <p className="spo-sync-range-modal__hint">{hint}</p> : null}
          <div className="spo-sync-range-modal__fields">
            <label className="spo-field spo-field--block">
              <span className="spo-field__label">
                {fixedUi('每状态最多页数')} (pageSize={pageSizeHint}, {fixedUi('默认')} {defaultMaxPages}, {fixedUi('上限')} {cap})
              </span>
              <input
                className="spo-input spo-product-sync-modal__input"
                type="number"
                min={1}
                max={cap}
                step={1}
                value={maxPages}
                disabled={busy}
                onChange={(e) => setMaxPages(e.target.value)}
              />
            </label>
          </div>
        </div>
        <div className="spo-batch-modal__foot">
          <button
            type="button"
            className="spo-btn spo-btn--ghost"
            disabled={busy}
            onClick={onClose}
          >
            {fixedUi("取消")}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary"
            disabled={busy}
            onClick={handleConfirm}
          >
            {busy ? fixedUi("启动中…") : fixedUi("开始同步")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
